import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth-context";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { DarkPage } from "@/components/app/PageHeader";
import { Plus, X, Trash2, Zap, ShieldAlert, ChevronDown, ChevronUp, History, Store, Package2 } from "lucide-react";

const ACTION_TARGETS = [
  { key: "assignee", label: "The record's assignee" },
  { key: "actor", label: "Whoever made the change" },
  { key: "admins", label: "Workspace admins" },
];

const PRIORITIES = ["low", "medium", "high", "critical"];

const SETTABLE_FIELDS = { task: ["priority"], project: ["status"] };

const EMPTY_ACTION = (type) => ({
  action_type: type,
  config: type === "notify" ? { target: "assignee", title: "", message: "" }
    : type === "create_task" ? { title: "", assignee: "actor", priority: "medium" }
    : type === "set_field" ? { field: "", value: "" }
    : { url: "" },
});

const EMPTY_FORM = { name: "", trigger_event: "", filter_value: "", actions: [] };

export default function Automations() {
  const { user } = useAuth();
  const isManager = user?.role === "admin" || user?.role === "owner";

  const [rules, setRules] = useState([]);
  const [triggers, setTriggers] = useState([]);
  const [actionTypes, setActionTypes] = useState([]);
  const [runs, setRuns] = useState({}); // rule_id -> runs[]
  const [expandedRuns, setExpandedRuns] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  const [selectMode, setSelectMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [showPublish, setShowPublish] = useState(false);
  const [publishForm, setPublishForm] = useState({ name: "", description: "", price: 0, currency: "GBP" });
  const [publishing, setPublishing] = useState(false);

  const load = useCallback(async () => {
    try {
      const [rRes, tRes, aRes] = await Promise.all([
        api.get("/automations/rules"),
        api.get("/automations/triggers"),
        api.get("/automations/action-types"),
      ]);
      setRules(rRes.data || []);
      setTriggers(tRes.data || []);
      setActionTypes(aRes.data || []);
    } catch {
      toast.error("Failed to load automations");
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const triggerMeta = (event) => triggers.find(t => t.event === event);
  const selectedTrigger = triggerMeta(form.trigger_event);

  const addAction = (type) => setForm(f => ({ ...f, actions: [...f.actions, EMPTY_ACTION(type)] }));
  const removeAction = (idx) => setForm(f => ({ ...f, actions: f.actions.filter((_, i) => i !== idx) }));
  const updateActionConfig = (idx, key, value) => setForm(f => ({
    ...f, actions: f.actions.map((a, i) => i === idx ? { ...a, config: { ...a.config, [key]: value } } : a),
  }));

  const create = async () => {
    if (!form.name.trim()) return toast.error("Name is required");
    if (!form.trigger_event) return toast.error("Choose a trigger");
    if (form.actions.length === 0) return toast.error("Add at least one action");
    setSaving(true);
    try {
      await api.post("/automations/rules", {
        name: form.name,
        trigger_event: form.trigger_event,
        trigger_filter: (selectedTrigger?.filter_field && form.filter_value)
          ? { field: selectedTrigger.filter_field, value: form.filter_value } : null,
        actions: form.actions,
      });
      toast.success("Automation created");
      setShowModal(false);
      setForm(EMPTY_FORM);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to create automation");
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (rule) => {
    try {
      await api.put(`/automations/rules/${rule.rule_id}`, { is_active: !rule.is_active });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to update");
    }
  };

  const remove = async (rule) => {
    if (!window.confirm(`Delete automation "${rule.name}"?`)) return;
    try {
      await api.delete(`/automations/rules/${rule.rule_id}`);
      toast.success("Deleted");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to delete");
    }
  };

  const toggleSelected = (ruleId) => setSelectedIds((ids) =>
    ids.includes(ruleId) ? ids.filter((id) => id !== ruleId) : [...ids, ruleId]
  );

  const openPublishDialog = () => {
    if (selectedIds.length === 0) return toast.error("Select at least one automation first");
    setPublishForm({ name: "", description: "", price: 0, currency: "GBP" });
    setShowPublish(true);
  };

  const publishPack = async () => {
    if (!publishForm.name.trim()) return toast.error("Name is required");
    setPublishing(true);
    try {
      const { data: pack } = await api.post("/automations/packs", {
        name: publishForm.name.trim(),
        description: publishForm.description,
        rule_ids: selectedIds,
      });
      await api.post(`/automations/packs/${pack.pack_id}/publish`, {
        price: Number(publishForm.price) || 0,
        currency: publishForm.currency,
      });
      toast.success("Published to the marketplace as an automation pack");
      setShowPublish(false);
      setSelectMode(false);
      setSelectedIds([]);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to publish pack");
    } finally {
      setPublishing(false);
    }
  };

  const toggleRuns = async (rule) => {
    if (expandedRuns === rule.rule_id) { setExpandedRuns(null); return; }
    setExpandedRuns(rule.rule_id);
    if (!runs[rule.rule_id]) {
      try {
        const { data } = await api.get(`/automations/runs?rule_id=${rule.rule_id}`);
        setRuns(r => ({ ...r, [rule.rule_id]: data }));
      } catch {
        toast.error("Failed to load run history");
      }
    }
  };

  if (!isManager) {
    return (
      <div className="p-10 text-center text-sm text-[#7d8590] flex flex-col items-center gap-2">
        <ShieldAlert className="w-6 h-6" />
        Only workspace owners and admins can manage automations.
      </div>
    );
  }

  return (
    <DarkPage><div className="p-6 max-w-4xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-2"><Zap className="w-5 h-5 text-[#7d8590]" /> Automations</h1>
          <p className="text-sm text-[#7d8590] mt-1">When something happens, do something automatically — notify someone, create a task, set a field, or call a webhook.</p>
        </div>
        <div className="flex items-center gap-2">
          <Link to="/marketplace">
            <Button variant="outline" className="border-[#30363d] text-[#7d8590] hover:text-white">
              <Store className="w-4 h-4 mr-1" /> Marketplace
            </Button>
          </Link>
          {selectMode ? (
            <>
              <Button onClick={openPublishDialog} disabled={selectedIds.length === 0} className="bg-[#00F5D4] text-[#030712] hover:bg-[#00F5D4]/90 font-semibold">
                <Package2 className="w-4 h-4 mr-1" /> Publish {selectedIds.length > 0 ? `${selectedIds.length} ` : ""}as pack
              </Button>
              <Button onClick={() => { setSelectMode(false); setSelectedIds([]); }} variant="outline" className="border-[#30363d] text-[#7d8590]">
                Cancel
              </Button>
            </>
          ) : (
            <Button onClick={() => setSelectMode(true)} variant="outline" className="border-[#30363d] text-[#7d8590] hover:text-white" disabled={rules.length === 0}>
              <Package2 className="w-4 h-4 mr-1" /> Publish as pack
            </Button>
          )}
          <Button onClick={() => { setForm(EMPTY_FORM); setShowModal(true); }} className="bg-[#00F5D4] text-[#030712] hover:bg-[#00F5D4]/90 font-semibold">
            <Plus className="w-4 h-4 mr-1" /> New automation
          </Button>
        </div>
      </div>

      {selectMode && (
        <div className="text-xs text-[#7d8590] mb-3 -mt-3">
          Select the automations to bundle into a sellable pack. Buyers get paused copies they review before activating.
        </div>
      )}

      {rules.length === 0 ? (
        <div className="text-center py-20 text-[#7d8590] text-sm">No automations yet.</div>
      ) : (
        <div className="space-y-3">
          {rules.map(rule => {
            const trig = triggerMeta(rule.trigger_event);
            return (
              <div key={rule.rule_id} className="bg-[#0d1117] border border-[#21262d] rounded-lg overflow-hidden">
                <div className="p-4 flex items-center justify-between gap-3 flex-wrap">
                  <div className="flex items-start gap-3">
                    {selectMode && (
                      <input
                        type="checkbox"
                        checked={selectedIds.includes(rule.rule_id)}
                        onChange={() => toggleSelected(rule.rule_id)}
                        className="mt-1.5 accent-[#00F5D4]"
                      />
                    )}
                    <div>
                    <div className="text-white font-medium flex items-center gap-2">
                      {rule.name}
                      <span className={`text-[10px] uppercase tracking-wide px-2 py-0.5 rounded-full border ${rule.is_active ? "text-[#00F5D4] bg-[#00F5D4]/10 border-[#00F5D4]/20" : "text-[#7d8590] bg-[#1c2128] border-[#30363d]"}`}>
                        {rule.is_active ? "active" : "paused"}
                      </span>
                    </div>
                    <div className="text-xs text-[#7d8590] mt-1">
                      When <span className="text-[#9CA3AF]">{trig?.label || rule.trigger_event}</span>
                      {rule.trigger_filter && <> and <span className="font-mono text-[#9CA3AF]">{trig?.filter_field_label} = {rule.trigger_filter.value}</span></>}
                      {" · "}{rule.actions.length} action{rule.actions.length === 1 ? "" : "s"}
                      {" · "}ran {rule.run_count} time{rule.run_count === 1 ? "" : "s"}
                    </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button onClick={() => toggleRuns(rule)} className="text-xs px-3 py-1.5 rounded-full border border-[#30363d] text-[#7d8590] hover:text-white flex items-center gap-1">
                      <History className="w-3 h-3" /> {expandedRuns === rule.rule_id ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                    </button>
                    <button onClick={() => toggleActive(rule)} className="text-xs px-3 py-1.5 rounded-full border border-[#30363d] text-[#7d8590] hover:text-white">
                      {rule.is_active ? "Pause" : "Resume"}
                    </button>
                    <button onClick={() => remove(rule)} className="text-[#484f58] hover:text-rose-400"><Trash2 className="w-4 h-4" /></button>
                  </div>
                </div>
                {expandedRuns === rule.rule_id && (
                  <div className="border-t border-[#21262d] p-4 bg-[#0a0d12]">
                    {!runs[rule.rule_id] ? (
                      <div className="text-xs text-[#7d8590]">Loading…</div>
                    ) : runs[rule.rule_id].length === 0 ? (
                      <div className="text-xs text-[#7d8590]">This automation hasn't run yet.</div>
                    ) : (
                      <div className="space-y-2">
                        {runs[rule.rule_id].map(r => (
                          <div key={r.run_id} className="text-xs flex items-center justify-between">
                            <span className="text-[#9CA3AF]">{new Date(r.triggered_at).toLocaleString()} · {r.entity_type} {r.entity_id}</span>
                            <span className={r.status === "success" ? "text-[#00F5D4]" : "text-amber-400"}>{r.status}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* New automation modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 overflow-y-auto py-8">
          <div className="bg-[#0d1117] border border-[#21262d] rounded-xl w-full max-w-lg p-6">
            <div className="flex items-center justify-between mb-5">
              <h2 className="text-white font-semibold">New automation</h2>
              <button onClick={() => setShowModal(false)} className="text-[#7d8590] hover:text-white"><X className="w-5 h-5" /></button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="text-xs text-[#7d8590] mb-1 block">Name *</label>
                <input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                  placeholder="e.g. Notify PM when a task is blocked"
                  className="w-full bg-[#161b22] border border-[#30363d] text-white rounded-md px-3 py-2 text-sm" />
              </div>
              <div>
                <label className="text-xs text-[#7d8590] mb-1 block">When… *</label>
                <select value={form.trigger_event} onChange={e => setForm(f => ({ ...f, trigger_event: e.target.value, filter_value: "" }))}
                  className="w-full bg-[#161b22] border border-[#30363d] text-white rounded-md px-3 py-2 text-sm">
                  <option value="">Select a trigger…</option>
                  {triggers.map(t => <option key={t.event} value={t.event}>{t.label}</option>)}
                </select>
              </div>
              {selectedTrigger?.filter_field && (
                <div>
                  <label className="text-xs text-[#7d8590] mb-1 block">Only when {selectedTrigger.filter_field_label.toLowerCase()} equals (optional)</label>
                  <input value={form.filter_value} onChange={e => setForm(f => ({ ...f, filter_value: e.target.value }))}
                    placeholder="Leave blank to match any value"
                    className="w-full bg-[#161b22] border border-[#30363d] text-white rounded-md px-3 py-2 text-sm" />
                </div>
              )}

              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-xs text-[#7d8590] block">Then… *</label>
                  <div className="flex gap-1">
                    {actionTypes.map(a => (
                      <button key={a.action_type} type="button" onClick={() => addAction(a.action_type)}
                        className="text-[10px] px-2 py-1 rounded-full border border-[#30363d] text-[#7d8590] hover:text-white hover:border-[#00F5D4]/30">
                        + {a.label}
                      </button>
                    ))}
                  </div>
                </div>
                {form.actions.length === 0 ? (
                  <div className="text-xs text-[#484f58] border border-dashed border-[#30363d] rounded-md p-3 text-center">No actions yet — add one above.</div>
                ) : (
                  <div className="space-y-3">
                    {form.actions.map((action, idx) => (
                      <div key={idx} className="border border-[#30363d] rounded-md p-3 space-y-2">
                        <div className="flex items-center justify-between">
                          <span className="text-xs text-white font-medium">{actionTypes.find(a => a.action_type === action.action_type)?.label}</span>
                          <button onClick={() => removeAction(idx)} className="text-[#484f58] hover:text-rose-400"><X className="w-3.5 h-3.5" /></button>
                        </div>

                        {action.action_type === "notify" && (
                          <>
                            <select value={action.config.target} onChange={e => updateActionConfig(idx, "target", e.target.value)}
                              className="w-full bg-[#161b22] border border-[#30363d] text-white rounded-md px-2 py-1.5 text-xs">
                              {ACTION_TARGETS.map(t => <option key={t.key} value={t.key}>{t.label}</option>)}
                            </select>
                            <input value={action.config.title} onChange={e => updateActionConfig(idx, "title", e.target.value)}
                              placeholder="Notification title"
                              className="w-full bg-[#161b22] border border-[#30363d] text-white rounded-md px-2 py-1.5 text-xs" />
                            <input value={action.config.message} onChange={e => updateActionConfig(idx, "message", e.target.value)}
                              placeholder="Message — use {{title}} or {{status}} to reference the record"
                              className="w-full bg-[#161b22] border border-[#30363d] text-white rounded-md px-2 py-1.5 text-xs" />
                          </>
                        )}

                        {action.action_type === "create_task" && (
                          <>
                            <input value={action.config.title} onChange={e => updateActionConfig(idx, "title", e.target.value)}
                              placeholder="Follow-up task title — use {{title}} to reference the record"
                              className="w-full bg-[#161b22] border border-[#30363d] text-white rounded-md px-2 py-1.5 text-xs" />
                            <div className="grid grid-cols-2 gap-2">
                              <select value={action.config.assignee} onChange={e => updateActionConfig(idx, "assignee", e.target.value)}
                                className="w-full bg-[#161b22] border border-[#30363d] text-white rounded-md px-2 py-1.5 text-xs">
                                {ACTION_TARGETS.map(t => <option key={t.key} value={t.key}>{t.label}</option>)}
                              </select>
                              <select value={action.config.priority} onChange={e => updateActionConfig(idx, "priority", e.target.value)}
                                className="w-full bg-[#161b22] border border-[#30363d] text-white rounded-md px-2 py-1.5 text-xs">
                                {PRIORITIES.map(p => <option key={p} value={p}>{p}</option>)}
                              </select>
                            </div>
                          </>
                        )}

                        {action.action_type === "set_field" && (
                          <div className="grid grid-cols-2 gap-2">
                            <select value={action.config.field} onChange={e => updateActionConfig(idx, "field", e.target.value)}
                              className="w-full bg-[#161b22] border border-[#30363d] text-white rounded-md px-2 py-1.5 text-xs">
                              <option value="">Field…</option>
                              {(SETTABLE_FIELDS[selectedTrigger?.entity_type] || []).map(f => <option key={f} value={f}>{f}</option>)}
                            </select>
                            <input value={action.config.value} onChange={e => updateActionConfig(idx, "value", e.target.value)}
                              placeholder="New value"
                              className="w-full bg-[#161b22] border border-[#30363d] text-white rounded-md px-2 py-1.5 text-xs" />
                          </div>
                        )}

                        {action.action_type === "webhook" && (
                          <input value={action.config.url} onChange={e => updateActionConfig(idx, "url", e.target.value)}
                            placeholder="https://…"
                            className="w-full bg-[#161b22] border border-[#30363d] text-white rounded-md px-2 py-1.5 text-xs font-mono" />
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
            <div className="flex gap-2 mt-5">
              <Button onClick={create} disabled={saving} className="flex-1 bg-[#00F5D4] text-[#030712] hover:bg-[#00F5D4]/90 font-semibold">
                {saving ? "Creating…" : "Create automation"}
              </Button>
              <Button onClick={() => setShowModal(false)} variant="outline" className="border-[#30363d] text-[#7d8590]">Cancel</Button>
            </div>
          </div>
        </div>
      )}

      {/* Publish-as-pack modal */}
      {showPublish && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 overflow-y-auto py-8">
          <div className="bg-[#0d1117] border border-[#21262d] rounded-xl w-full max-w-md p-6">
            <div className="flex items-center justify-between mb-5">
              <h2 className="text-white font-semibold">Publish automation pack</h2>
              <button onClick={() => setShowPublish(false)} className="text-[#7d8590] hover:text-white"><X className="w-5 h-5" /></button>
            </div>
            <div className="space-y-4">
              <p className="text-sm text-[#7d8590]">
                Bundling <strong className="text-white">{selectedIds.length}</strong> automation{selectedIds.length === 1 ? "" : "s"} into a pack.
                Buyers install paused copies into their own workspace.
              </p>
              <div>
                <label className="text-xs text-[#7d8590] mb-1 block">Pack name *</label>
                <input value={publishForm.name} onChange={e => setPublishForm(f => ({ ...f, name: e.target.value }))}
                  placeholder="e.g. Client onboarding automation pack"
                  className="w-full bg-[#161b22] border border-[#30363d] text-white rounded-md px-3 py-2 text-sm" />
              </div>
              <div>
                <label className="text-xs text-[#7d8590] mb-1 block">Description</label>
                <textarea value={publishForm.description} onChange={e => setPublishForm(f => ({ ...f, description: e.target.value }))}
                  placeholder="What does this pack do?"
                  rows={3}
                  className="w-full bg-[#161b22] border border-[#30363d] text-white rounded-md px-3 py-2 text-sm" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-[#7d8590] mb-1 block">Price</label>
                  <input type="number" min="0" step="1" value={publishForm.price}
                    onChange={e => setPublishForm(f => ({ ...f, price: e.target.value }))}
                    className="w-full bg-[#161b22] border border-[#30363d] text-white rounded-md px-3 py-2 text-sm font-mono" />
                </div>
                <div>
                  <label className="text-xs text-[#7d8590] mb-1 block">Currency</label>
                  <select value={publishForm.currency} onChange={e => setPublishForm(f => ({ ...f, currency: e.target.value }))}
                    className="w-full bg-[#161b22] border border-[#30363d] text-white rounded-md px-3 py-2 text-sm">
                    <option>GBP</option>
                    <option>USD</option>
                    <option>EUR</option>
                  </select>
                </div>
              </div>
            </div>
            <div className="flex gap-2 mt-5">
              <Button onClick={publishPack} disabled={publishing} className="flex-1 bg-[#00F5D4] text-[#030712] hover:bg-[#00F5D4]/90 font-semibold">
                {publishing ? "Publishing…" : (Number(publishForm.price) > 0 ? `Publish for ${publishForm.currency} ${publishForm.price}` : "Publish as free")}
              </Button>
              <Button onClick={() => setShowPublish(false)} variant="outline" className="border-[#30363d] text-[#7d8590]">Cancel</Button>
            </div>
          </div>
        </div>
      )}
    </div></DarkPage>
  );
}
